"use client"

import { dateLib, moneyLib, roomLib } from "@agentcall/core/lib"
import { tenantType } from "@agentcall/core/type"
import { useQueryClient } from "@tanstack/react-query"
import { subDays } from "date-fns"
import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { roomApi } from "@/api/client/room"
import { tenantApi } from "@/api/client/tenant"
import type { TenantCreateBody } from "@/api/generated/models"
import { getErrorMessage } from "@/api/instance"
import { PropertyHeader } from "@/components/properties/PropertyHeader"
import { RoomHeader } from "@/components/properties/RoomHeader"
import { TenantForm, type TenantFormValues } from "@/components/properties/TenantForm"
import { Button } from "@/components/ui/button/button"
import { DatePicker } from "@/components/ui/date-picker/date-picker"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog/dialog"
import { toOccupantsPayload } from "@/pages/new-tenant-register/components"

export function TenantAddPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { propertyId: propertyIdParam, roomId: roomIdParam } = useParams()
  const parsedPropertyId = Number(propertyIdParam)
  const propertyId = Number.isFinite(parsedPropertyId) ? parsedPropertyId : undefined
  const parsedRoomId = Number(roomIdParam)
  const roomId = Number.isFinite(parsedRoomId) ? parsedRoomId : undefined
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [pendingTenantData, setPendingTenantData] = useState<TenantCreateBody | null>(null)
  const [currentTenantEndDate, setCurrentTenantEndDate] = useState<Date | undefined>(undefined)
  const [isEndDateDialogOpen, setIsEndDateDialogOpen] = useState(false)

  const { data: roomResponse } = roomApi.useRoomGet(
    roomId,
    { withTenants: "true" },
    {
      query: {
        enabled: roomId !== undefined,
      },
    }
  )
  const currentTenant = roomLib.roomCurrentTenant(roomResponse?.room)

  const tenantCreateMutation = tenantApi.useTenantCreate({
    mutation: {
      onSuccess: () => {
        toast.success("賃借人情報を登録しました")
        queryClient.invalidateQueries({ queryKey: ["/properties"] })
        if (propertyId !== undefined && roomId !== undefined) {
          navigate(`/properties/${propertyId}/rooms/${roomId}`, { state: { shouldRefetch: true } })
          return
        }
        navigate("/properties")
      },
      onError: (error) => {
        toast.error("賃借人情報の登録に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })
  const tenantCancelMutation = tenantApi.useTenantCancel({
    mutation: {
      onError: (error) => {
        toast.error("現在の賃借人の終了に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const navigateToRoom = () => {
    if (propertyId !== undefined && roomId !== undefined) {
      navigate(`/properties/${propertyId}/rooms/${roomId}`)
      return
    }

    if (propertyId !== undefined) {
      navigate(`/properties/${propertyId}`)
      return
    }

    navigate("/properties")
  }

  const buildTenantCreateData = (values: TenantFormValues): TenantCreateBody => {
    const toNumber = (value?: string) => {
      const parsed = Number(value ?? 0)
      return Number.isFinite(parsed) ? parsed : 0
    }

    // 未入力の行は送らない（新規入居者登録フォームと同じ扱い）
    const otherFees = values.otherFees
      .filter((fee) => fee.name && fee.baseFee)
      .map((fee) => ({
        label: fee.name,
        price: toNumber(fee.baseFee),
        tax: toNumber(fee.tax),
      }))

    // 駐車場代は税込みで保存（税抜き金額 + 税額）
    const parkingBaseFee = toNumber(values.parkingFee)
    const parkingFeeWithTax = parkingBaseFee + moneyLib.calculateTax(parkingBaseFee)

    return {
      roomId: roomId ?? 0,
      name: values.fullName,
      nameKana: values.fullNameKana,
      phone1: values.phone1,
      phone2: values.phone2,
      phone3: values.phone3,
      phone4: values.phone4,
      phone5: values.phone5,
      phone6: values.phone6,
      email: values.email ?? "",
      // 賃借人の生年月日はこのフォームでは入力しない
      birthday: null,
      startDate: values.startDate,
      rent: toNumber(values.rent),
      commonAreaFee: toNumber(values.maintenanceFee),
      isParking: values.hasParking,
      parkingFee: parkingFeeWithTax,
      parkingNumber: values.parkingLotNumber ?? "",
      otherFees,
      purpose: values.purpose,
      occupants: toOccupantsPayload(values.occupants),
    }
  }

  const submitTenantCreate = async (data: TenantCreateBody) => {
    await tenantCreateMutation.mutateAsync({ data })
  }

  const openCurrentTenantEndDateDialog = (data: TenantCreateBody) => {
    const dayBeforeNextTenantStart = subDays(new Date(data.startDate), 1)
    setPendingTenantData(data)
    setCurrentTenantEndDate(dayBeforeNextTenantStart)
    setIsEndDateDialogOpen(true)
  }

  const handleSubmit = async (values: TenantFormValues) => {
    if (roomId === undefined) {
      navigateToRoom()
      return
    }

    const data = buildTenantCreateData(values)

    if (currentTenant && !currentTenant.endDate) {
      openCurrentTenantEndDateDialog(data)
      return
    }

    setIsSubmitting(true)
    try {
      await submitTenantCreate(data)
    } catch (error) {
      console.error("Error submitting tenant:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleConfirmCurrentTenantEndDate = async () => {
    if (!currentTenant || !pendingTenantData || !currentTenantEndDate) {
      return
    }

    const endDate = dateLib.formatDateISO(currentTenantEndDate)
    if (endDate >= pendingTenantData.startDate) {
      toast.error("現在の賃借人の終了日は、次の賃借人の開始日より前の日付を選択してください")
      return
    }

    setIsSubmitting(true)
    try {
      await tenantCancelMutation.mutateAsync({
        id: currentTenant.id,
        data: {
          endDate,
          endReason: tenantType.TENANT_END_REASON_TENANT_MOVE_OUT,
        },
      })
      await submitTenantCreate(pendingTenantData)
      setIsEndDateDialogOpen(false)
      setPendingTenantData(null)
      setCurrentTenantEndDate(undefined)
    } catch (error) {
      console.error("Error submitting tenant:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!propertyId || !roomId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-slate-600">物件IDまたは部屋IDが不正です。</div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <PropertyHeader propertyId={propertyId} />
      <RoomHeader roomId={roomId} />

      <TenantForm onSubmit={handleSubmit} onCancel={navigateToRoom} isSubmitting={isSubmitting} />

      <Dialog open={isEndDateDialogOpen} onOpenChange={setIsEndDateDialogOpen}>
        <DialogContent className="max-w-[420px] rounded-[10px] px-5 py-[30px]">
          <DialogTitle className="text-center text-lg font-semibold text-[#354267]">
            現在の賃借人の終了日を選択
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            次の賃借人を登録する前に、現在の賃借人の賃貸借契約終了日を入力してください。
          </DialogDescription>
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-400">賃貸借契約終了日</p>
            <DatePicker
              value={currentTenantEndDate}
              onChange={setCurrentTenantEndDate}
              placeholder="日付を選択"
              fromDate={currentTenant?.startDate ? new Date(currentTenant.startDate) : undefined}
              toDate={
                pendingTenantData?.startDate
                  ? subDays(new Date(pendingTenantData.startDate), 1)
                  : undefined
              }
            />
            {pendingTenantData?.startDate && (
              <p className="text-xs text-slate-500">
                次の賃借人の開始日: {pendingTenantData.startDate}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setIsEndDateDialogOpen(false)}
              disabled={isSubmitting}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              className="w-full"
              onClick={handleConfirmCurrentTenantEndDate}
              disabled={!currentTenantEndDate}
              loading={isSubmitting}
            >
              登録する
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
